import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { MenuItem, TextField, Typography } from '@mui/material';
import { Box, Grid } from '@mui/system';

import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { CourseSelect } from 'app/components/CourseSelect';
import { translations } from 'locales/i18n';

// Define sort types
export type SortDirection = 'asc' | 'desc' | null;
export type SortField = 'title' | 'jobType' | 'publication' | null;

interface Props {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  paid: string | undefined;
  setPaid: Dispatch<SetStateAction<string>>;
  course: string | undefined;
  setCourse: Dispatch<SetStateAction<string>>;
  sortField: SortField;
  setSortField: Dispatch<SetStateAction<SortField>>;
  sortDirection: SortDirection;
  setSortDirection: Dispatch<SetStateAction<SortDirection>>;
}

export const FilterHeader = ({
  paid,
  setPaid,
  search,
  setSearch,
  course,
  setCourse,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection
}: Props) => {
  const { t } = useTranslation();

  // Sort state
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      // Set new field and direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper to render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;

    if (sortDirection === 'asc') {
      return <ArrowUpward sx={{ fontSize: 18, ml: 0.5 }} />;
    } else if (sortDirection === 'desc') {
      return <ArrowDownward sx={{ fontSize: 18, ml: 0.5 }} />;
    }

    return null;
  };

  return (
    <Grid container justifyContent={'space-between'} gap={2} marginBottom={2}>
      <Grid container gap={2}>
        <CourseSelect course={course} setCourse={setCourse} />
        <TextField
          size="small"
          placeholder={t(translations.pages.jobOffers.searchbarTitle())}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <TextField
          select
          size="small"
          label={t(translations.pages.manageJobOffers.paid())}
          value={paid}
          onChange={e => setPaid(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem key={'---'} value={''}>
            ---
          </MenuItem>
          <MenuItem value={'true'}>{t(translations.common.yes())}</MenuItem>
          <MenuItem value={'false'}>{t(translations.common.no())}</MenuItem>
        </TextField>
      </Grid>
      {/* Table Header */}
      <Grid container size={12} sx={{ display: 'flex' }}>
        <Box
          onClick={() => handleSort('title')}
          sx={{
            flex: 1,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            justifyContent: 'center',
            border: 1,
            borderColor: 'lightgray',
            backgroundColor: sortField === 'title' ? 'orange' : undefined,
            borderRadius: '8px 0 0 8px'
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {t(translations.pages.jobOffers.searchbarTitle())}
            {renderSortIcon('title')}
          </Typography>
        </Box>
        <Box
          onClick={() => handleSort('jobType')}
          sx={{
            flex: 1,
            p: 1.5,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            borderColor: 'lightgray',
            backgroundColor: sortField === 'jobType' ? 'orange' : undefined
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {t(translations.common.type())}
            {renderSortIcon('jobType')}
          </Typography>
        </Box>
        <Box
          onClick={() => handleSort('publication')}
          sx={{
            flex: 1,
            p: 1.5,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            borderColor: 'lightgray',
            backgroundColor: sortField === 'publication' ? 'orange' : undefined,
            borderRadius: '0 8px 8px 0'
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {t(translations.pages.jobOffers.createdAt())}
            {renderSortIcon('publication')}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};
